import {
    Component,
    OnDestroy,
    OnInit,
    ViewChildren,
    ViewContainerRef
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { map, switchMap, tap } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { BreadcrumbProvider, Crumb } from '../../../breadcrumb/providers/breadcrumb-provider';
import { BehaviorSubject, forkJoin, Observable, of, Subject, Subscription } from 'rxjs';
import { Organization } from "../../../common/models/organization";
import { AthleteRow, AthletesProvider } from "../../providers/athletes-provider";
import { Agenda } from "../../../common/models/agenda";
import { AthleteRights } from "../../providers/athletes-rights";
import { CurrentUser } from "../../../common/providers/current-user";
import { ConfigProvider } from "../../../config-provider.service";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { CalendarDayData, TimeslotsProvider } from "../../../agendas/providers/timeslots-provider";
import { AgendaService } from "../../../agendas/agenda.service";
import { AgendaProvider } from "../../../agendas/providers/agenda-provider";
import { OrganizationRole } from "../../../common/models/organization-role";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { AgendasCalendarComponent } from "../../../agendas/components/calendar/calendar";
import { FeedbackBoxComponent } from "../../components/feedback-box/feedback-box";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { SideMenuComponent } from "../../../common/components/side-menu/side-menu";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { AnnualPlanBoxComponent } from "../../components/annual-plan-box/annual-plan-box";
import { TestBoxComponent } from "../../components/test-box/test-box";
import { AthleteSideMenuComponent } from "../../components/side-menu/side-menu";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { EvaluationBoxComponent } from "../../components/evaluation-box/evaluation-box";
import { MatIcon } from "@angular/material/icon";
import { NgIf } from "@angular/common";
import { MatMiniFabButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";
import { FlexLayoutModule } from "@angular/flex-layout";
import {
  CommunicationBoxComponent
} from "../../../communications/components/communication-box/communication-box.component";
import { UploadComponent } from "../../../document/components/upload/upload.component";
import { User } from "../../../common/models/user";
import { LastTestBoxComponent } from "../../components/last-test-box/last-test-box";

@Component({
  selector: 'athlete-view-page',
  providers: [],
  styleUrls: ['./view.scss'],
  standalone: true,
  imports: [
    PageContentComponent,
    HeaderComponent,
    AgendasCalendarComponent,
    FeedbackBoxComponent,
    ModuleContentComponent,
    SideMenuComponent,
    Error401Component,
    AnnualPlanBoxComponent,
    TestBoxComponent,
    AthleteSideMenuComponent,
    Error404Component,
    EvaluationBoxComponent,
    MatIcon,
    NgIf,
    MatMiniFabButton,
    MatTooltip,
    FlexLayoutModule,
    CommunicationBoxComponent,
    UploadComponent,
    TranslateModule,
    LastTestBoxComponent
  ],
  templateUrl: './view.html'
})
export class AthleteViewPageComponent implements OnInit, OnDestroy {

    public pageTitle = 'Athlete View';
    public isLoading = true;
    public is401 = false;
    public is404 = false;
    public organization: Organization = {} as Organization;
    public viewedAthlete: AthleteRow = {} as AthleteRow;

    public timeSegments = 2;
    public collapseBefore: string = this._utils.DateToHm(new Date(0, 0, 0, 8, 0));
    public collapseAfter: string = this._utils.DateToHm(new Date(0, 0, 0, 18, 59));
    public get currentDate() {
      return this._currentDate;
    }
    public agenda: Agenda = {} as Agenda;
    public agendaData: CalendarDayData[] = [];
    public evaluationAthleteData: any[] = [];
    public rights: AthleteRights = new AthleteRights(this._utils);

    @ViewChildren('calendarRef', { read: ViewContainerRef })
    public calendarRef: ViewContainerRef | undefined;

    private _currentDate: Date = new Date();
    protected athleteUser: User = {} as User;

    private subscriptions: Subscription[] = [];

    constructor(
        public conf: ConfigProvider,
        public breadcrumbProvider: BreadcrumbProvider,
        public currentUser: CurrentUser,
        private _utils: UtilsProvider,
        private _title: Title,
        private _currentUserRoles: CurrentUserRolesProvider,
        private _route: ActivatedRoute,
        private _translater: TranslateService,
        private _timeslotsProvider: TimeslotsProvider,
        private _athletesProvider: AthletesProvider,
        private agendaProvider: AgendaProvider,
        private _router: Router,
        private agendaService: AgendaService,
    ) {
    }

    public ngOnInit() {
      this.subscriptions.push(
        this._route.data.subscribe((data: any) => { // Properly type this based on your route data structure
          this.organization = data.organization;
          this.athleteUser = data.athlete;
          this.initPage();
        })
      );
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public onOpenCalendarClicked(): void {
        if (!this.organization || !this.athleteUser || !this.agenda) {
            return;
        }
        const url = '/' + this.organization!.code + '/athletes/' + this.athleteUser.id + '/calendar/' + this.agenda.id + '/' + this._utils.DateToYmd(this.currentDate) + (this.viewedAthlete?.rights?.edit ? '/edit' : '') + '?backto=' + this._router.url;
        this._router.navigateByUrl(url);
    }

    private initPage(): void {
        this.loadData().subscribe({
          next: () => {

            this.subscriptions.push(
              this.agendaService.currentDateSubject.subscribe({
                next: (date: Date) => {
                  this._currentDate = date;
                  if (!this.agenda || !this.agenda.id) return;
                  this._timeslotsProvider.getCalendarWeekData(this.organization!, this.agenda.id, this._currentDate, this.timeSegments)
                    .subscribe((agendaData: CalendarDayData[]) => {
                        this.agendaData = agendaData;
                      },
                    );
                },
              })
            );

            this.initNav();
            this.isLoading = false;
          },
          error: (error: any) => this.onError(error)
        })
    }

    private initNav(): void {

        if (!this.viewedAthlete?.rights?.view) {
            this.is401 = true;
            return;
        }

        this.createPageTitle();
        this.createBreadcrumb();
    }

    private loadData(): Observable<void> {
      if (!this.organization?.id || !this.athleteUser?.id ) {
        console.error('Organization or athlete not found');
        this.onError({ error: 'Organization or athlete not found', status: 404 });
        this.is404 = true;
        return of();
      }

      this.viewedAthlete = this._athletesProvider.createAthleteRow(this.organization.id, this.athleteUser);

      return forkJoin([
        of(this._currentUserRoles.getRolesIds(this.organization.id)),
        this.agendaProvider.getAthleteAgenda(this.organization.id, this.athleteUser.id),
      ]).pipe(
        switchMap(([rolesIds, athleteAgenda]: [number[], Agenda]) => {
          this.agenda = athleteAgenda;
          if (!this.agenda?.id) throw new Error('Agenda not found');
          this.agendaService.emitDateChanged(this._currentDate);

          return this._timeslotsProvider.getCalendarWeekData(this.organization, this.agenda.id, this.currentDate, this.timeSegments).pipe(
            map(agendaData => {
              this.agendaData = agendaData;
              this.rights.evaluateScreenRights({
                rolesIds: rolesIds
              });
            })
          );
        }),
      );
    }

    private createPageTitle() {
      if (!this.viewedAthlete) {
            return;
        }
        this.pageTitle = this.viewedAthlete.user.firstName + ' ' + this.viewedAthlete.user.lastName + ' | ' + this._translater.instant('Athletes');
        this._title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
    }

    private createBreadcrumb() {
        if (!this.organization?.id || !this.viewedAthlete) {
            return;
        }
        const crumbs: Crumb[] = [];
        if (!this._currentUserRoles.have(OrganizationRole.ID_ATHLETE, this.organization.id)) {
            crumbs.push({
                shortTitle: 'Athletes',
                title: 'Athletes',
                url: '/' + this.organization!.code + '/athletes'
            });
        }
        crumbs.push({
            shortTitle: this.viewedAthlete.user.firstName + ' ' + this.viewedAthlete.user.lastName,
            title: this.viewedAthlete.user.firstName + ' ' + this.viewedAthlete.user.lastName,
            url: '/' + this.organization!.code + '/athletes/' + this.viewedAthlete.user.id
        });
        this.breadcrumbProvider.setDefaultCrumbs(crumbs);
    }

    private onError(error: any): void {
        this.isLoading = false;

        if (error.status === 404) {
            // this.is404 = true;
        } else if (error.status === 401) {
            this.is401 = true;
        }
    }
}
